import { useCallback, useSyncExternalStore } from "react";
import { useAuth } from "../../hooks/AuthProvider";

// Fixed by the OTP service, shown read-only in the panels
export const OTP_CODE_LENGTH = 6;
export const OTP_MAX_ATTEMPTS = 3;
export const OTP_CONTEXTS = ["login", "checkout", "reset", "signup"];
export const SAMPLE_OTP_CODE = "482913";
export const DEFAULT_CHAT_NUMBER = "{{sender_phone}}";
export const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

// The last sandbox OTP created by a Send "Test API" run, shared by the Verify and Resend panels
let lastSandboxResult = null;
const listeners = new Set();

export const setLastSandboxResult = (result) => {
  lastSandboxResult = result;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useLastSandboxResult = () =>
  useSyncExternalStore(subscribe, () => lastSandboxResult);

// Authenticated call to a v2 OTP endpoint as the logged-in user. Resolves to { status, body }
export const useOtpApi = () => {
  const { getValidToken } = useAuth();

  return useCallback(
    async (url, { method = "POST", body } = {}) => {
      const token = await getValidToken();
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": localStorage.getItem("tenant"),
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        // Empty or non-JSON body
      }
      return { status: response.status, body: data };
    },
    [getValidToken],
  );
};

export const findNodeConfig = (nodes, nodeTypeKey) =>
  (nodes || []).find((node) => node.node_type_key === nodeTypeKey)?.config || {};

// The Generate request body the Send node produces, for the live preview and for Test API
export const buildSendBody = (config, templateConfig, phone) => ({
  phone: phone ?? (config.phone || DEFAULT_CHAT_NUMBER),
  context: config.context || "login",
  ttl_seconds: config.ttl_seconds || 300,
  template_name: templateConfig.template_name || "",
  variables: templateConfig.variables || {},
});

export const formatApiResult = (result) =>
  `HTTP ${result.status}\n${JSON.stringify(result.body, null, 2)}`;
